import {
  createButton,
  createColorPicker,
  createPanel,
  createSelect,
  createTextInput
} from "./components";
import {
  createInfoDocumentView,
  createMoreInfoView,
  type InfoDocumentKey
} from "./menu_info";
import { LANGUAGE_OPTIONS, type I18n, type Language } from "../i18n/i18n";
import type { SavedGameMode, SavedProfile, SavedSettings } from "../storage/storage";
import {
  PLAYER_NAME_MAX_LENGTH,
  chooseReadableNameColor,
  sanitizeName
} from "../storage/storage";
import { drawPlayer } from "../render/entity_drawers";
import { drawFittedName } from "../render/entity_names";
import type { RgbColor, SnapshotEntity } from "../render/types";
import { createAdSlot } from "../ads/ad_slots";

export type MainMenuController = {
  element: HTMLElement;
  getMode: () => SavedGameMode;
  getProfile: () => SavedProfile;
  getLanguage: () => Language;
  getSoundEnabled: () => boolean;
  setStatus: (text: string) => void;
  destroy: () => void;
};

export type MainMenuOptions = {
  settings: SavedSettings;
  i18n: I18n;
  onPlay: (mode: SavedGameMode, profile: SavedProfile, language: Language) => void;
  onProfileChange: (profile: SavedProfile) => void;
  onLanguageChange: (language: Language) => void;
  onSoundEnabledChange: (soundEnabled: boolean) => void;
};

export type GameScreenController = {
  element: HTMLElement;
  canvas: HTMLCanvasElement;
  setStatus: (text: string) => void;
  destroy: () => void;
};

type FullscreenCapableElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenCapableDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

type MenuView = "main" | "moreInfo" | "document";

export function createMainMenu(options: MainMenuOptions): MainMenuController {
  let selectedMode = options.settings.lastMode;
  let profile = { ...options.settings.profile };
  let language = options.settings.language;
  let soundEnabled = options.settings.soundEnabled;
  let activeView: MenuView = "main";
  let activeDocument: InfoDocumentKey | null = null;

  const shell = document.createElement("div");
  shell.className = "menu-ad-shell";

  const leftAd = createAdSlot("menu-left", "ad-slot-menu-side");
  const rightAd = createAdSlot("menu-right", "ad-slot-menu-side");
  const bottomAd = createAdSlot("menu-bottom", "ad-slot-menu-bottom");

  const screen = document.createElement("main");
  screen.className = "menu-screen";

  const title = document.createElement("h1");
  title.className = "menu-title-outside";
  title.textContent = options.i18n.t("app.title");

  const panel = createPanel("menu-panel");

  const topBar = document.createElement("div");
  topBar.className = "menu-top-bar";

  const soundButton = document.createElement("button");
  soundButton.type = "button";
  soundButton.className = "menu-sound-button";
  soundButton.addEventListener("click", () => {
    soundEnabled = !soundEnabled;
    options.onSoundEnabledChange(soundEnabled);
    syncSoundButton();
  });

  const moreInfoButton = createButton({
    text: options.i18n.t("menu.moreInfo"),
    variant: "secondary",
    onClick: () => {
      if (activeView === "main") {
        showMoreInfoView();
      } else {
        showMainView();
      }
    }
  });
  moreInfoButton.classList.add("menu-more-button");

  const languageSelect = createSelect<Language>({
    label: "",
    value: language,
    options: [...LANGUAGE_OPTIONS],
    onChange: (nextLanguage: Language) => {
      language = nextLanguage;
      options.onLanguageChange(nextLanguage);
      syncLocalizedText();

      if (activeView === "moreInfo") {
        showMoreInfoView();
      } else if (activeView === "document" && activeDocument) {
        showDocumentView(activeDocument);
      }
    }
  });

  languageSelect.classList.add("menu-language-field");
  topBar.append(soundButton, moreInfoButton, languageSelect);

  const viewHost = document.createElement("div");
  viewHost.className = "menu-view-host";

  const content = document.createElement("div");
  content.className = "menu-content";

  const previewColumn = document.createElement("section");
  previewColumn.className = "menu-preview-column";

  const previewCard = document.createElement("div");
  previewCard.className = "player-preview-card";

  const previewStage = document.createElement("div");
  previewStage.className = "player-preview-stage";

  const previewCanvas = document.createElement("canvas");
  previewCanvas.className = "player-preview-canvas";
  previewCanvas.style.display = "block";
  previewCanvas.style.width = "clamp(190px, 22vw, 280px)";
  previewCanvas.style.height = "clamp(190px, 22vw, 280px)";

  previewStage.append(previewCanvas);
  previewCard.append(previewStage);
  previewColumn.append(previewCard);

  const previewResizeObserver =
    typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(() => {
          updatePreview();
        });

  previewResizeObserver?.observe(previewStage);

  const formColumn = document.createElement("section");
  formColumn.className = "menu-form-column";

  const modeRow = document.createElement("div");
  modeRow.className = "menu-mode-row";

  const offlineButton = createButton({
    text: options.i18n.t("menu.offline"),
    variant: selectedMode === "offline" ? "primary" : "secondary",
    onClick: () => {
      selectedMode = "offline";
      syncModeButtons();
    }
  });

  const onlineButton = createButton({
    text: options.i18n.t("menu.online"),
    variant: selectedMode === "online" ? "primary" : "secondary",
    onClick: () => {
      selectedMode = "online";
      syncModeButtons();
    }
  });

  modeRow.append(onlineButton, offlineButton);

  const nameInput = createTextInput({
    label: options.i18n.t("menu.name"),
    value: profile.name,
    maxLength: PLAYER_NAME_MAX_LENGTH,
    onInput: (name: string) => {
      profile = { ...profile, name: sanitizeName(name) };
      syncProfileInputs();
      updatePreview();
      updatePlayState();
      options.onProfileChange(profile);
    }
  });

  const colorGrid = document.createElement("div");
  colorGrid.className = "menu-color-grid";

  const ballColor = createColorPicker({
    label: options.i18n.t("menu.ballColor"),
    value: profile.ballColor,
    onInput: (color: string) => {
      const previousNameColor = profile.nameColor;
      profile = { ...profile, ballColor: color };

      if (previousNameColor === "#ffffff" || previousNameColor === "#000000") {
        profile = { ...profile, nameColor: chooseReadableNameColor(color) };
      }

      syncProfileInputs();
      updatePreview();
      options.onProfileChange(profile);
    }
  });

  const nameColor = createColorPicker({
    label: options.i18n.t("menu.nameColor"),
    value: profile.nameColor,
    onInput: (color: string) => {
      profile = { ...profile, nameColor: color };
      syncProfileInputs();
      updatePreview();
      options.onProfileChange(profile);
    }
  });

  colorGrid.append(ballColor, nameColor);

  const actions = document.createElement("div");
  actions.className = "menu-actions";

  const playButton = createButton({
    text: options.i18n.t("menu.play"),
    variant: "primary",
    onClick: () => {
      if (profile.name.trim().length === 0) {
        status.textContent = options.i18n.t("menu.nameRequired");
        return;
      }

      options.onPlay(selectedMode, profile, language);
    }
  });

  playButton.classList.add("menu-play-button");

  const tapOnGamesBanner = document.createElement("a");
  tapOnGamesBanner.className = "menu-cross-promo-link";
  tapOnGamesBanner.href = "https://tapongames.com/";
  tapOnGamesBanner.target = "_blank";
  tapOnGamesBanner.rel = "noopener noreferrer";
  tapOnGamesBanner.setAttribute("aria-label", "Visit TapOnGames.com");

  const tapOnGamesBannerImage = document.createElement("img");
  tapOnGamesBannerImage.className = "menu-cross-promo-image";
  tapOnGamesBannerImage.src = "/images/tapongames_ad_banner.png";
  tapOnGamesBannerImage.alt = "TapOnGames.com";
  tapOnGamesBannerImage.loading = "eager";
  tapOnGamesBannerImage.decoding = "async";

  tapOnGamesBanner.append(tapOnGamesBannerImage);

  const status = document.createElement("p");
  status.className = "menu-status";
  status.textContent = "";

  actions.append(playButton, tapOnGamesBanner, status);
  formColumn.append(modeRow, nameInput, colorGrid, actions);
  content.append(previewColumn, formColumn);

  panel.append(topBar, viewHost);
  screen.append(title, panel);
  shell.append(leftAd, screen, rightAd, bottomAd);

  syncSoundButton();
  syncModeButtons();
  syncProfileInputs();
  updatePreview();
  updatePlayState();
  requestAnimationFrame(updatePreview);
  showMainView();

  return {
    element: shell,
    getMode: () => selectedMode,
    getProfile: () => profile,
    getLanguage: () => language,
    getSoundEnabled: () => soundEnabled,
    setStatus: (text: string) => {
      status.textContent = text;
    },
    destroy: () => {
      previewResizeObserver?.disconnect();
      shell.remove();
    }
  };

  function showMainView(): void {
    activeView = "main";
    activeDocument = null;
    moreInfoButton.classList.remove("menu-more-button-active");
    viewHost.replaceChildren(content);
    updatePreview();
    updatePlayState();
  }

  function showMoreInfoView(): void {
    activeView = "moreInfo";
    activeDocument = null;
    moreInfoButton.classList.add("menu-more-button-active");
    viewHost.replaceChildren(
      createMoreInfoView({
        i18n: options.i18n,
        onBack: showMainView,
        onDocument: showDocumentView
      })
    );
  }

  function showDocumentView(documentKey: InfoDocumentKey): void {
    activeView = "document";
    activeDocument = documentKey;
    moreInfoButton.classList.add("menu-more-button-active");
    viewHost.replaceChildren(
      createInfoDocumentView({
        i18n: options.i18n,
        documentKey,
        onBack: showMoreInfoView
      })
    );
  }

  function updatePreview(): void {
    if (activeView !== "main") {
      return;
    }

    const context = previewCanvas.getContext("2d");

    if (!context) {
      return;
    }

    const stageRect = previewStage.getBoundingClientRect();
    const cssSize = Math.round(clamp(stageRect.width * 0.86, 190, 280));
    const ratio = window.devicePixelRatio || 1;

    previewCanvas.width = Math.max(1, Math.round(cssSize * ratio));
    previewCanvas.height = Math.max(1, Math.round(cssSize * ratio));
    previewCanvas.style.width = `${cssSize}px`;
    previewCanvas.style.height = `${cssSize}px`;

    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, cssSize, cssSize);

    const radius = clamp(cssSize * 0.29, 58, 68);
    const centerX = cssSize * 0.5;
    const centerY = cssSize * 0.5;
    const entity = createPreviewEntity(centerX, centerY, radius);

    drawPlayer(context, centerX, centerY, radius, entity, null);
    drawFittedName(context, centerX, centerY, radius, entity);
  }

  function createPreviewEntity(x: number, y: number, radius: number): SnapshotEntity {
    return {
      id: "preview",
      kind: "player",
      position: {
        x,
        y
      },
      radius,
      name: profile.name,
      cosmetics: {
        ball_color: hexToRgb(profile.ballColor),
        name_color: hexToRgb(profile.nameColor)
      }
    };
  }

  function updatePlayState(): void {
    playButton.disabled = profile.name.trim().length === 0;
    playButton.classList.toggle("ui-button-disabled", playButton.disabled);
    status.textContent = playButton.disabled ? options.i18n.t("menu.nameRequired") : "";
  }

  function syncProfileInputs(): void {
    const textInput = nameInput.querySelector("input");
    const ballColorInput = ballColor.querySelector(".web-color-hex");
    const ballColorPreview = ballColor.querySelector(".web-color-preview");
    const nameColorInput = nameColor.querySelector(".web-color-hex");
    const nameColorPreview = nameColor.querySelector(".web-color-preview");

    if (textInput instanceof HTMLInputElement && textInput.value !== profile.name) {
      textInput.value = profile.name;
    }

    if (ballColorInput instanceof HTMLInputElement) {
      ballColorInput.value = profile.ballColor;
    }

    if (ballColorPreview instanceof HTMLElement) {
      ballColorPreview.style.background = profile.ballColor;
    }

    if (nameColorInput instanceof HTMLInputElement) {
      nameColorInput.value = profile.nameColor;
    }

    if (nameColorPreview instanceof HTMLElement) {
      nameColorPreview.style.background = profile.nameColor;
    }
  }

  function syncLocalizedText(): void {
    title.textContent = options.i18n.t("app.title");
    moreInfoButton.textContent = options.i18n.t("menu.moreInfo");
    onlineButton.textContent = options.i18n.t("menu.online");
    offlineButton.textContent = options.i18n.t("menu.offline");
    playButton.textContent = options.i18n.t("menu.play");

    const nameLabel = nameInput.querySelector(".ui-label");
    const ballColorLabel = ballColor.querySelector(".ui-label");
    const nameColorLabel = nameColor.querySelector(".ui-label");

    if (nameLabel instanceof HTMLElement) {
      nameLabel.textContent = options.i18n.t("menu.name");
    }

    if (ballColorLabel instanceof HTMLElement) {
      ballColorLabel.textContent = options.i18n.t("menu.ballColor");
    }

    if (nameColorLabel instanceof HTMLElement) {
      nameColorLabel.textContent = options.i18n.t("menu.nameColor");
    }

    if (activeView === "main") {
      updatePlayState();
    }
  }

  function syncModeButtons(): void {
    onlineButton.className =
      selectedMode === "online" ? "ui-button ui-button-primary" : "ui-button ui-button-secondary";
    offlineButton.className =
      selectedMode === "offline" ? "ui-button ui-button-primary" : "ui-button ui-button-secondary";
  }

  function syncSoundButton(): void {
    soundButton.innerHTML = soundEnabled ? soundOnIcon() : soundOffIcon();
    soundButton.title = soundEnabled ? "Sound on" : "Sound off";
    soundButton.setAttribute("aria-label", soundEnabled ? "Sound on" : "Sound off");
  }
}

export function createGameScreen(statusText = ""): GameScreenController {
  const screen = document.createElement("main");
  screen.className = "game-screen";

  const canvas = document.createElement("canvas");
  canvas.className = "game-canvas";

  const status = document.createElement("div");
  status.className = "game-status";
  status.textContent = statusText;

  const fullscreenButton = document.createElement("button");
  fullscreenButton.type = "button";
  fullscreenButton.className = "game-fullscreen-button";
  fullscreenButton.addEventListener("click", () => {
    void toggleFullscreen();
  });

  if (!isFullscreenSupported()) {
    fullscreenButton.hidden = true;
  }

  document.addEventListener("fullscreenchange", syncFullscreenButton);
  document.addEventListener("webkitfullscreenchange", syncFullscreenButton);

  screen.append(canvas, status, fullscreenButton);
  syncFullscreenButton();

  return {
    element: screen,
    canvas,
    setStatus: (text: string) => {
      status.textContent = text;
    },
    destroy: () => {
      document.removeEventListener("fullscreenchange", syncFullscreenButton);
      document.removeEventListener("webkitfullscreenchange", syncFullscreenButton);
      screen.remove();
    }
  };

  async function toggleFullscreen(): Promise<void> {
    try {
      if (isFullscreenActive()) {
        await exitFullscreen();
      } else {
        await requestFullscreen(document.documentElement);
      }
    } catch {
      fullscreenButton.classList.add("game-fullscreen-button-error");
      window.setTimeout(() => {
        fullscreenButton.classList.remove("game-fullscreen-button-error");
      }, 700);
    }

    syncFullscreenButton();
  }

  function syncFullscreenButton(): void {
    const active = isFullscreenActive();

    screen.classList.toggle("game-screen-fullscreen", active);
    fullscreenButton.classList.toggle("game-fullscreen-button-active", active);
    fullscreenButton.innerHTML = active ? fullscreenExitIcon() : fullscreenEnterIcon();
    fullscreenButton.title = active ? "Exit fullscreen" : "Enter fullscreen";
    fullscreenButton.setAttribute(
      "aria-label",
      active ? "Exit fullscreen" : "Enter fullscreen"
    );
  }
}

function isFullscreenSupported(): boolean {
  const element = document.documentElement as FullscreenCapableElement;
  const value = document.fullscreenEnabled || typeof element.webkitRequestFullscreen === "function";

  return Boolean(value);
}

function isFullscreenActive(): boolean {
  const fullscreenDocument = document as FullscreenCapableDocument;

  return Boolean(document.fullscreenElement || fullscreenDocument.webkitFullscreenElement);
}

async function requestFullscreen(element: HTMLElement): Promise<void> {
  const fullscreenElement = element as FullscreenCapableElement;

  if (typeof fullscreenElement.requestFullscreen === "function") {
    await fullscreenElement.requestFullscreen();
    return;
  }

  if (typeof fullscreenElement.webkitRequestFullscreen === "function") {
    await fullscreenElement.webkitRequestFullscreen();
  }
}

async function exitFullscreen(): Promise<void> {
  const fullscreenDocument = document as FullscreenCapableDocument;

  if (typeof document.exitFullscreen === "function") {
    await document.exitFullscreen();
    return;
  }

  if (typeof fullscreenDocument.webkitExitFullscreen === "function") {
    await fullscreenDocument.webkitExitFullscreen();
  }
}

function fullscreenEnterIcon(): string {
  return `
    <svg class="game-fullscreen-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 9V4h5v2H6v3H4Zm11-3V4h5v5h-2V6h-3Zm3 9h2v5h-5v-2h3v-3ZM6 15v3h3v2H4v-5h2Z"></path>
    </svg>
  `;
}

function fullscreenExitIcon(): string {
  return `
    <svg class="game-fullscreen-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M9 4v5H4V7h3V4h2Zm6 0h2v3h3v2h-5V4ZM4 15h5v5H7v-3H4v-2Zm13 2v3h-2v-5h5v2h-3Z"></path>
    </svg>
  `;
}

export function exitFullscreenIfActive(): void {
  if (!isFullscreenActive()) {
    return;
  }

  void exitFullscreen();
}

export function createLoadingScreen(title: string, subtitle: string): HTMLElement {
  const screen = document.createElement("main");
  screen.className = "center-screen";

  const panel = createPanel("status-panel");

  const heading = document.createElement("h1");
  heading.className = "status-title";
  heading.textContent = title;

  const text = document.createElement("p");
  text.className = "status-text";
  text.textContent = subtitle;

  panel.append(heading, text);
  screen.append(panel);

  return screen;
}

export function replaceRoot(root: HTMLElement, element: HTMLElement): void {
  root.innerHTML = "";
  root.append(element);
}

export function showError(root: HTMLElement, message: string): void {
  root.innerHTML = "";

  const screen = document.createElement("main");
  screen.className = "center-screen";

  const panel = createPanel("status-panel");

  const title = document.createElement("h1");
  title.className = "status-title";
  title.textContent = "Error";

  const text = document.createElement("p");
  text.className = "status-text";
  text.textContent = message;

  panel.append(title, text);
  screen.append(panel);
  root.append(screen);
}

function soundOnIcon(): string {
  return `
    <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true" focusable="false">
      <g transform="translate(0 0.5)">
        <path d="M4.5 10H8l5-4.3v14.6L8 16H4.5z" fill="currentColor"/>
        <path d="M16.1 9.2c1.05 1.05 1.6 2.35 1.6 3.8s-.55 2.75-1.6 3.8" fill="none" stroke="currentColor" stroke-width="2.15" stroke-linecap="round"/>
        <path d="M18.85 6.65c1.95 1.8 3.05 4.02 3.05 6.35s-1.1 4.55-3.05 6.35" fill="none" stroke="currentColor" stroke-width="2.15" stroke-linecap="round"/>
      </g>
    </svg>
  `;
}

function soundOffIcon(): string {
  return `
    <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true" focusable="false">
      <g transform="translate(0 0.5)">
        <path d="M4.5 10H8l5-4.3v14.6L8 16H4.5z" fill="currentColor"/>
        <path d="M17.35 10.35l5.3 5.3" fill="none" stroke="currentColor" stroke-width="2.35" stroke-linecap="round"/>
        <path d="M22.65 10.35l-5.3 5.3" fill="none" stroke="currentColor" stroke-width="2.35" stroke-linecap="round"/>
      </g>
    </svg>
  `;
}

function hexToRgb(hex: string): RgbColor {
  const normalized = hex.replace("#", "");
  const value = Number.parseInt(normalized, 16);

  if (!Number.isFinite(value) || normalized.length !== 6) {
    return {
      r: 66,
      g: 135,
      b: 245
    };
  }

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}